import React, { useEffect, useState } from "react";
import { Table, Button, Dropdown, Space, Modal } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import { deleteLive, editStatus, getAllLives } from "../../services/live";
import ButtonDefault, { ButtonVariants } from "../shared/basic/button";
import { notify } from "../shared/basic/notify";
import extractErrorMessage from "../shared/basic/formateError";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { ShouldRender } from "../shared/basic/ShouldRender";
import moment from "moment";
import clsx from "clsx";

interface LiveStream {
  key: string;
  thumbnail: string;
  title: string;
  author_name: string;
  author_zl_uid: string;
  createdDate: string;
  date: string;
  status: "Approved" | "Pending" | "Completed";
}

const LiveStreamDashboard = () => {
  const [activeTab, setActiveTab] = useState("Approved");
  const [liveData, setLiveData] = useState<LiveStream[]>([]);
  const [isModalOpenDelete, setIsModalOpenDelete] = useState<{
    value: boolean;
    id: string;
  }>({ value: false, id: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const router = useNavigate();
  const screens = useBreakpoint();

  const tabs = [
    { key: "Approved", label: "Approved" },
    { key: "Pending", label: "Draft Live" },
    { key: "Completed", label: "Completed Live" },
  ];

  const handleOk = () => {
    handleDeleteLive(isModalOpenDelete.id);
  };

  const handleCancel = () => {
    setIsModalOpenDelete({
      id: "",
      value: false,
    });
  };

  const handleDeleteLive = async (id: string) => {
    try {
      setDeleteLoading(true);
      await deleteLive(id);
      await handleAllLives(
        formatStatus(activeTab).toLowerCase() as
          | "approved"
          | "pending"
          | "completed"
      );

      setIsModalOpenDelete({
        id: "",
        value: false,
      });
    } catch (error: any) {
      const message = extractErrorMessage(error);
      notify(message, "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderStatusButton = (status: string, id: string) => {
    let className = "text-white border-none rounded-lg flex items-center";

    if (status === "Approved") {
      className += " bg-[#26BD6C4D]";
    } else if (status === "Pending") {
      className += " bg-[#F48E2F40]";
    } else if (status === "Completed") {
      className += " bg-[#E6483D4D]";
    }

    const handleMenuClick = ({ key }: { key: string }) => {
      let newStatus = "";

      if (key === "1") newStatus = "Approved";
      else if (key === "2") newStatus = "Pending";
      else if (key === "3") newStatus = "Completed";

      if (newStatus && newStatus !== status) {
        updateStatus(id, newStatus);
      }
    };

    return (
      <Dropdown
        menu={{
          items: [
            { key: "1", label: "Approved" },
            { key: "2", label: "Pending" },
            { key: "3", label: "Completed" },
          ],
          onClick: handleMenuClick,
        }}
        trigger={["click"]}
      >
        <Button className={className}>
          <Space>
            {status}
            <ChevronDownIcon className="text-sm w-4 h-5" />
          </Space>
        </Button>
      </Dropdown>
    );
  };

  // Table columns configuration
  const columns = [
    {
      title: "Thumbnail",
      dataIndex: "thumbnail",
      key: "thumbnail",
      render: (thumbnail: string) => (
        <div className="w-[152px] h-[80px] overflow-hidden rounded">
          <img
            src={thumbnail || "/placeholder.svg"}
            alt="Thumbnail"
            className="w-full h-full object-cover"
          />
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title: string) => (
        <div className="w-[20vw] lg:w-[30vw] h overflow-hidden rounded">
          {title}
        </div>
      ),
    },
    {
      title: "Speaker",

      key: "author_name",
      render: (speaker: { author_name: string }) => {
        return (
          <div className="text-[#6199FF] whitespace-nowrap  flex">
            {speaker?.author_name}
          </div>
        );
      },
    },
    {
      title: <div className="whitespace-nowrap">Created Date</div>,
      dataIndex: "createdDate",
      key: "createdDate",
      render: (createdDate: string) => {
        return (
          <div className=" whitespace-nowrap">
            {moment(createdDate).format("DD MMMM, YYYY")}
          </div>
        );
      },
    },
    {
      title: <div className="whitespace-nowrap">Live Date</div>,
      dataIndex: "date",
      key: "date",
      render: (createdDate: string) => {
        return (
          <div className=" whitespace-nowrap">
            {moment(createdDate).format("DD MMMM, YYYY")}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: { key: string }) =>
        renderStatusButton(status, record.key),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: { key: string }) => {
        return (
          <Space size="middle">
            <Button
              type="text"
              onClick={() => router(`/live/edit-live/${record.key}`)}
              icon={<EditOutlined />}
            />
            <Button
              type="text"
              onClick={() =>
                setIsModalOpenDelete({
                  id: record.key,
                  value: true,
                })
              }
              icon={<DeleteOutlined />}
            />
            {/* <Button className="bg-[#14151A] text-white rounded-[10px] hover:bg-gray-700">
              View
            </Button> */}
          </Space>
        );
      },
    },
  ];

  const handleAllLives = async (
    statusFilter: "approved" | "pending" | "completed"
  ) => {
    try {
      const lives = await getAllLives(statusFilter);

      const formattedLives: LiveStream[] = lives?.data?.map((item: any) => ({
        key: String(item.id),
        thumbnail: item.thumbnail,
        title: item.title,
        date: item.date,
        author_name: item.author_name || "Unknown",
        author_zl_uid: item.author_zl_uid || "N/A",
        createdDate: item.created_at,
        status: formatStatus(item.status),
      }));

      setLiveData(formattedLives);
    } catch (error) {
      console.error("API call error:", error);
    }
  };

  const formatStatus = (
    status: string
  ): "Approved" | "Pending" | "Completed" => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "Approved";
      case "completed":
        return "Completed";
      default:
        return "Pending";
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await editStatus(id, status?.toLowerCase());
      await handleAllLives(
        formatStatus(activeTab).toLowerCase() as
          | "approved"
          | "pending"
          | "completed"
      );
    } catch (error) {
      console.error("API call error:", error);
    }
  };

  useEffect(() => {
    handleAllLives(
      formatStatus(activeTab).toLowerCase() as
        | "approved"
        | "pending"
        | "completed"
    );
  }, [activeTab]);

  return (
    <div className="  text-white">
      <div className="flex flex-col-reverse gap-y-4 lg:flex-row justify-between lg:items-center mb-4 w-full">
        <div className="bg-[#FFFFFF14] rounded-[12px] px-2 py-1 flex relative">
          <motion.div
            className="absolute inset-y-1 rounded-[10px] z-0"
            initial={false}
            animate={{
              width: `calc(100% / ${tabs.length})`,
              left: `calc(${tabs.findIndex(
                (t) => t.key === activeTab
              )} * (100% / ${tabs.length}))`,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
            }}
            style={{
              backgroundColor: "#0B0C0E",
            }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 28,
              mass: 1.2,
            }}
            layoutId="tab-background"
          />
          {tabs.map((tab) => (
            <motion.button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={clsx(
                "flex-1 xs:min-w-[100px] sm:min-w-[120px] lg:min-w-[200px] px-3 py-2 mx-1 text-white whitespace-nowrap text-xs md:text-sm font-medium rounded-[10px] justify-center relative z-10",
                activeTab === tab.key ? "" : "hover:bg-zinc-800/50"
              )}
              animate={{
                opacity: activeTab === tab.key ? 1 : 0.7,
                scale: activeTab === tab.key ? 1.02 : 1,
              }}
              transition={{
                opacity: { duration: 0.2 },
                scale: { duration: 0.15, type: "spring", stiffness: 300 },
              }}
              whileHover={{
                opacity: activeTab === tab.key ? 1 : 0.85,
                scale: activeTab === tab.key ? 1.02 : 1.01,
                transition: { duration: 0.1 },
              }}
              whileTap={{ scale: 0.98 }}
            >
              {tab.label}
            </motion.button>
          ))}
        </div>

        <ShouldRender check={!screens.lg}>
          <div className="flex justify-between items-center w-full">
            <div className="text-xl font-medium">Live Screen</div>

            <Button
              type="primary"
              onClick={() => {
                router(`/live/create-live`);
              }}
              icon={<PlusOutlined />}
              className="border-none"
            >
              Create New Live
            </Button>
          </div>
        </ShouldRender>
        <ShouldRender check={screens.lg}>
          <Button
            type="primary"
            onClick={() => {
              router(`/live/create-live`);
            }}
            icon={<PlusOutlined />}
            className="border-none"
          >
            Create New Live
          </Button>
        </ShouldRender>
      </div>

      <Table
        columns={columns}
        dataSource={liveData}
        className="border-[#FFFFFF24]  border-x rounded-[12px] overflow-x-auto"
        pagination={false}
        rowClassName="bg-[#0B0C0E] "
      />

      <Modal
        open={isModalOpenDelete.value}
        onOk={handleOk}
        onCancel={handleCancel}
        width={350}
        closable={false}
        className="customMaskModal rounded-2xl"
        footer={null}
      >
        <div className="bg-[#121212] p-2 text-white border rounded-2xl border-[#3D3D3D]  ">
          <div className="w-full flex flex-col gap-y-4 justify-center py-2">
            <div className="flex justify-center gap-x-2 py-5">
              <div className=" flex-col flex gap-y-4">
                <span className="text-center">
                  <ExclamationCircleFilled
                    className="text-yellow-400"
                    style={{ fontSize: " 30px" }}
                  />
                </span>
                <span className="text-lg">Sure want to delete this live ?</span>
              </div>
            </div>
            <div className="flex justify-center px-5 md:px-10 gap-x-4">
              <ButtonDefault
                size={1}
                variant={ButtonVariants.UNSTYLED}
                className="w-full p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0"
                onClick={handleCancel}
              >
                <span className="flex text-sm gap-x-2 px-2  whitespace-nowrap">
                  No
                </span>
              </ButtonDefault>

              <Button
                disabled={deleteLoading}
                loading={deleteLoading}
                type="primary"
                className="w-full"
                onClick={handleOk}
              >
                Yes
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LiveStreamDashboard;
